import { useState } from "react";
import { ArrowLeft, Plus } from "lucide-react";
import { LabOrderDetails } from "pages/patient/PatientLabOrdersPage/components/LabOrderDetails/LabOrderDetails";
import { LabOrderIbHandoff } from "pages/patient/PatientLabOrdersPage/components/LabOrderIbHandoff/LabOrderIbHandoff";
import { LabOrderNotices } from "pages/patient/PatientLabOrdersPage/components/LabOrderNotices/LabOrderNotices";
import { LabOrdersTable } from "pages/patient/PatientLabOrdersPage/components/LabOrdersTable/LabOrdersTable";
import { LabOrderStatusPill } from "pages/patient/PatientLabOrdersPage/components/LabOrderStatusPill/LabOrderStatusPill";
import { LabOrderSteps } from "pages/patient/PatientLabOrdersPage/components/LabOrderSteps/LabOrderSteps";
import { NewLabOrderForm } from "pages/patient/PatientLabOrdersPage/components/NewLabOrderForm/NewLabOrderForm";
import { useIbLabOrderReturn } from "pages/patient/PatientLabOrdersPage/hooks/useIbLabOrderReturn";
import { labOrdersViews } from "pages/patient/PatientLabOrdersPage/hooks/useLabOrdersView";
// Reuses PatientLabOrdersPage's own layout classes rather than duplicating them — its
// CSS module already says the referrals and lab order screens are meant to read as
// one, and this tab is that same flow reached from the consult instead of the eLabs
// nav item.
import s from "pages/patient/PatientLabOrdersPage/PatientLabOrdersPage.module.css";

import { HealthlinkCredentialsModal } from "components/HealthlinkCredentialsModal/HealthlinkCredentialsModal.jsx";
import { ProcessMessagesButton } from "components/ProcessMessagesButton/ProcessMessagesButton";
import { Button } from "components/ui";
import { Dialog } from "components/ui/Dialog/Dialog";
import { useHealthlinkCredentials } from "features/healthlink/hooks/useHealthlinkCredentials";
import { useHealthlinkPractice } from "features/healthlink/hooks/useHealthlinkPractice";
import { useLabOrder } from "features/labOrders/hooks/useLabOrder";
import { useLabOrderFacilities } from "features/labOrders/hooks/useLabOrderFacilities";
import { areMockLabFacilitiesEnabled } from "features/labOrders/mockFacilities";
import { getLabOrderPreflightViolations } from "features/labOrders/utils/labOrderPreflight";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { formatDate } from "utils/dateFormatter";

// eLab ordering, embedded in the consult next to Add Referral (the other Healthlink
// action a GP takes mid-consultation). Lab orders carry no consultationId — the
// backend never scoped them to one — so, unlike ReferralTab, this shows every order
// for the patient rather than only the active consultation's.
export const LabOrderTab = ({ isReadOnly = false }) => {
  const patientId = useCurrentPatientId();
  // Opens straight on the new-order form rather than the list — selecting this tab
  // mid-consultation means "place an order", not "browse past ones".
  const [view, setView] = useState(
    isReadOnly ? labOrdersViews.list : labOrdersViews.newOrder
  );
  // Only set when the redirect out to Healthlink did not happen — then the GP gets the
  // copy-link fallback, exactly as a referral does.
  const [failedRedirect, setFailedRedirect] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data: healthlinkPractice } = useHealthlinkPractice();
  const { data: healthlinkCredentials } = useHealthlinkCredentials();
  const { data: practiceSettings } = usePracticeSettings();
  const { data: patient } = usePatientData(patientId);
  const {
    data: facilities,
    isLoading: areFacilitiesLoading,
    isError: hasFacilitiesError,
    refetch: refetchFacilities,
  } = useLabOrderFacilities();

  // Confirm-on-return from the Healthlink Integrated Browser; keeps the list hidden
  // until the returned order has been settled.
  const { isProcessingReturn } = useIbLabOrderReturn();

  const hasHealthlinkCredentials =
    !!healthlinkPractice &&
    Array.isArray(healthlinkCredentials) &&
    healthlinkCredentials.length > 0;

  const preflightViolations = getLabOrderPreflightViolations(patient);
  // Laboratory ordering is a per-practice feature flag (KIN-1532). Compared against
  // false explicitly, so a backend that does not report it yet is not read as "off".
  const isOrderingDisabled = practiceSettings?.labOrdering === false;
  const hasNoFacilities =
    !areFacilitiesLoading && !hasFacilitiesError && facilities?.length === 0;
  const canPlaceOrder =
    hasHealthlinkCredentials &&
    !isOrderingDisabled &&
    !hasNoFacilities &&
    preflightViolations.length === 0;

  const returnToList = () => {
    setFailedRedirect(null);
    setSelectedOrder(null);
    setView(labOrdersViews.list);
  };

  // Shares its query key with the detail view's own call, so React Query serves both
  // from one request — the header must not show a status the section below contradicts.
  const { data: openedOrder } = useLabOrder(selectedOrder?.id);
  const detailOrder = openedOrder ?? selectedOrder;

  return (
    <div className={s.column}>
      <LabOrderNotices
        patientId={patientId}
        preflightViolations={preflightViolations}
        isOrderingDisabled={isOrderingDisabled}
        hasNoFacilities={hasNoFacilities}
        areMockFacilitiesEnabled={areMockLabFacilitiesEnabled()}
      />

      {view === labOrdersViews.list && (
        <div className={s.mainWrapper}>
          <div className={s.titleWrapper}>
            <h3 className={s.title}>Lab Orders</h3>
            <div className={s.headerActions}>
              <ProcessMessagesButton
                title="Process lab order responses"
                successMessage="Lab order responses processed successfully!"
              />
              {!isReadOnly &&
                (hasHealthlinkCredentials ? (
                  <Button
                    variant="primary"
                    endIcon={Plus}
                    onClick={() => setView(labOrdersViews.newOrder)}
                    disabled={!canPlaceOrder}
                  >
                    Add lab order
                  </Button>
                ) : (
                  <HealthlinkCredentialsModal />
                ))}
            </div>
          </div>
          <div className={s.content}>
            <LabOrdersTable
              patientId={patientId}
              isBusy={isProcessingReturn}
              onSelectOrder={(order) => {
                setSelectedOrder(order);
                setView(labOrdersViews.details);
              }}
            />
          </div>
        </div>
      )}

      {view === labOrdersViews.newOrder && !isReadOnly && (
        <div className={`${s.mainWrapper} ${s.mainWrapperFlush}`}>
          <div className={`${s.titleWrapper} ${s.titleWrapperPadded}`}>
            <div className={s.backButtonWrapper}>
              <Button
                variant="outline"
                size="small"
                className={s.backButton}
                aria-label="Back to lab orders"
                onClick={returnToList}
              >
                <ArrowLeft />
              </Button>
              <h3 className={s.title}>New lab order</h3>
            </div>
          </div>

          <LabOrderSteps />

          <div className={s.content}>
            <NewLabOrderForm
              patientId={patientId}
              patient={patient}
              facilities={facilities}
              areFacilitiesLoading={areFacilitiesLoading}
              hasFacilitiesError={hasFacilitiesError}
              onRetryFacilities={refetchFacilities}
              isBlockedByPatientDetails={preflightViolations.length > 0}
              onRedirectFailed={setFailedRedirect}
              onBack={returnToList}
            />
          </div>
        </div>
      )}

      {view === labOrdersViews.details && !!selectedOrder && (
        <div className={`${s.mainWrapper} ${s.mainWrapperFlush}`}>
          <div className={`${s.titleWrapper} ${s.titleWrapperPadded}`}>
            <div className={s.backButtonWrapper}>
              <Button
                variant="outline"
                size="small"
                className={s.backButton}
                aria-label="Back to lab orders"
                onClick={returnToList}
              >
                <ArrowLeft />
              </Button>
              <h3 className={s.title}>
                Lab order{" "}
                <span className={s.titleDate}>
                  {formatDate(selectedOrder.created)}
                </span>
              </h3>
            </div>
            <LabOrderStatusPill order={detailOrder} />
          </div>
          <div className={s.previewPanel}>
            <LabOrderDetails
              order={selectedOrder}
              onOrderCompleted={returnToList}
            />
          </div>
        </div>
      )}

      {/* Shown only when the redirect out to Healthlink did not take — the GP finishes the
          order from the copy-link instead. The same fallback the referral form ends on. */}
      <Dialog
        open={!!failedRedirect}
        title="Complete lab order in Healthlink"
        onClose={() => setFailedRedirect(null)}
        width={640}
      >
        <LabOrderIbHandoff
          submission={failedRedirect}
          onFinished={returnToList}
          isFlush
        />
      </Dialog>
    </div>
  );
};
